import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronDownIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { reviewApi, errorMessage } from '../api';
import { isFieldVisible, isInputField, allFields } from '../engine';
import AnswerView, { sectionSummary } from '../AnswerView';
import { Eyebrow, Panel, StatusPill, READINESS_LABELS, formatDate } from '../ui';
import { useAuth } from '../../../context/AuthContext';

const ACTIVITY_LABELS = {
    started: 'Started',
    submitted: 'Submitted',
    resubmitted: 'Resubmitted',
    checklist_completed: 'Checklist completed',
    returned: 'Returned for changes',
    approved: 'Approved',
    recalled: 'Recalled by student',
    nudged: 'Reminder sent'
};

const STATUS_LABEL = {
    submitted: 'Submitted · awaiting you',
    changes_requested: 'Changes requested',
    approved: 'Approved',
    in_progress: 'In progress'
};

export default function ChallengeReviewDetail() {
    const { submissionId } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(() => new Set());
    const [feedback, setFeedback] = useState('');
    const [deciding, setDeciding] = useState(null);

    useEffect(() => {
        reviewApi.get(submissionId)
            .then((d) => {
                setData(d);
                setFeedback(d.submission.mentor_feedback || '');
                const sections = d.challenge.sections.filter((s) => (s.fields || []).some(isInputField));
                const commented = new Set((d.submission.comments || []).map((c) => c.section_key).filter(Boolean));
                setOpen(new Set(commented.size ? commented : [sections[0] && sections[0].key]));
            })
            .catch((err) => setError(errorMessage(err, 'Could not load this submission')));
    }, [submissionId]);

    const fieldSection = useMemo(() => {
        const out = {};
        if (data) for (const { section, field } of allFields(data.challenge)) if (field.key) out[field.key] = section.key;
        return out;
    }, [data]);

    if (error) return <Panel><p className="text-sm text-red-700">{error}</p><Link to="/mentor/challenge-reviews" className="btn-secondary mt-4 inline-flex">Back to reviews</Link></Panel>;
    if (!data) return <div className="space-y-4"><div className="h-10 w-80 animate-pulse rounded bg-ink-100" /><div className="h-96 animate-pulse rounded-2xl bg-ink-100" /></div>;

    const { challenge, student, submission } = data;
    const answers = submission.answers || {};
    const canComment = submission.status !== 'approved';
    const sections = challenge.sections.filter((s) => (s.fields || []).some(isInputField));
    const headlines = allFields(challenge)
        .filter(({ field }) => field.headline && answers[field.key] && (answers[field.key].value))
        .map(({ field }) => {
            const opt = (field.options || []).find((o) => o.value === answers[field.key].value);
            return `${field.headline}: ${opt ? opt.label : answers[field.key].value}`;
        });

    const toggle = (key) => setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const addComment = async (body) => {
        try {
            const comment = await reviewApi.comment(submission.id, body);
            setData((d) => ({ ...d, submission: { ...d.submission, comments: [...d.submission.comments, comment] } }));
            return true;
        } catch (err) {
            toast.error(errorMessage(err, 'Could not add the comment'));
            return false;
        }
    };

    const removeComment = async (commentId) => {
        try {
            await reviewApi.deleteComment(submission.id, commentId);
            setData((d) => ({ ...d, submission: { ...d.submission, comments: d.submission.comments.filter((c) => c.id !== commentId) } }));
        } catch (err) {
            toast.error(errorMessage(err, 'Could not delete the comment'));
        }
    };

    const decide = async (decision) => {
        setDeciding(decision);
        try {
            const next = await reviewApi.decide(submission.id, { decision, feedback });
            setData(next);
            toast.success(decision === 'approve' ? 'Approved — the student\'s next stage is unlocked' : 'Returned to the student with your comments');
        } catch (err) {
            toast.error(errorMessage(err, 'Could not save your decision'));
        } finally {
            setDeciding(null);
        }
    };

    return (
        <div className="mx-auto w-full max-w-6xl space-y-5 pb-12">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="min-w-0">
                    <Eyebrow>
                        <Link to="/mentor/challenge-reviews" className="underline decoration-ink-300 underline-offset-4 hover:text-brand-700">Reviews</Link>
                        <span className="mx-1.5">/</span>{student.name}
                    </Eyebrow>
                    <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">{challenge.title} — {student.name}</h1>
                    <p className="mt-1 text-sm text-ink-600">
                        {[submission.submitted_at ? `Submitted ${formatDate(submission.submitted_at)}` : 'Not submitted yet', ...headlines].join(' · ')}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                    <StatusPill status={submission.status} label={STATUS_LABEL[submission.status]} />
                    <a href={`/mentor/challenge-reviews/${submission.id}/print`} target="_blank" rel="noreferrer" className="btn-secondary">Download PDF</a>
                </div>
            </header>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                    {sections.map((section) => {
                        const isOpen = open.has(section.key);
                        const sectionComments = submission.comments.filter((c) => (c.section_key === section.key || fieldSection[c.field_key] === section.key));
                        return (
                            <section key={section.key} className="overflow-hidden rounded-2xl border border-border bg-white">
                                <button
                                    type="button" onClick={() => toggle(section.key)} aria-expanded={isOpen}
                                    className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-ink-50/50"
                                >
                                    <span className="text-[15px] font-semibold text-ink-950">{section.key} · {section.title}</span>
                                    <span className="flex items-center gap-2 text-xs text-ink-500">
                                        {sectionComments.length > 0 && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-sky-800">{sectionComments.length} comment{sectionComments.length === 1 ? '' : 's'}</span>}
                                        {sectionSummary(section, answers, submission.files, submission.score)}
                                        {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="space-y-1 border-t border-border px-5 pb-5 pt-2">
                                        {section.fields.map((field, i) => {
                                            if (field.type === 'heading') {
                                                return <p key={`h${i}`} className="pt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">{field.title}</p>;
                                            }
                                            if (!isInputField(field) || !isFieldVisible(field, answers)) return null;
                                            return (
                                                <ReviewField
                                                    key={field.key}
                                                    field={field}
                                                    answer={answers[field.key]}
                                                    files={submission.files.filter((f) => f.field_key === field.key)}
                                                    comments={submission.comments.filter((c) => c.field_key === field.key)}
                                                    canComment={canComment}
                                                    userId={user && user.id}
                                                    onAdd={(text) => addComment({ field_key: field.key, section_key: section.key, text })}
                                                    onRemove={removeComment}
                                                />
                                            );
                                        })}
                                        <SectionCommentBox
                                            comments={submission.comments.filter((c) => !c.field_key && c.section_key === section.key)}
                                            canComment={canComment}
                                            userId={user && user.id}
                                            onAdd={(text) => addComment({ section_key: section.key, text })}
                                            onRemove={removeComment}
                                        />
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
                    <Glance challenge={challenge} submission={submission} />

                    <Panel className={submission.status === 'submitted' ? 'border-brand-300' : ''}>
                        <h2 className="text-[15px] font-semibold text-ink-950">Your decision</h2>
                        {submission.status === 'submitted' ? (
                            <>
                                <label htmlFor="overall-feedback" className="mt-3 block text-xs font-semibold text-ink-800">Overall feedback to student</label>
                                <textarea
                                    id="overall-feedback" rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)}
                                    className="mt-1.5 w-full resize-y rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
                                    placeholder="What went well, and what should change?"
                                />
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <button type="button" disabled={Boolean(deciding)} onClick={() => decide('return')} className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60">
                                        {deciding === 'return' ? 'Returning…' : 'Return for changes'}
                                    </button>
                                    <button type="button" disabled={Boolean(deciding)} onClick={() => decide('approve')} className="btn-primary justify-center disabled:opacity-60">
                                        {deciding === 'approve' ? 'Approving…' : 'Approve'}
                                    </button>
                                </div>
                                <p className="mt-3 text-xs leading-relaxed text-ink-500">Approving unlocks the student's next stage. Returning sends your comments and reopens the workbook for editing.</p>
                            </>
                        ) : (
                            <div className="mt-2 text-sm text-ink-700">
                                {submission.status === 'approved' && <p>Approved{submission.reviewer_name ? ` by ${submission.reviewer_name}` : ''} on {formatDate(submission.reviewed_at)}.</p>}
                                {submission.status === 'changes_requested' && <p>Returned for changes on {formatDate(submission.reviewed_at)}. Waiting for the student to resubmit.</p>}
                                {submission.status === 'in_progress' && <p>The student is still working on this challenge.</p>}
                                {submission.mentor_feedback && <p className="mt-2 whitespace-pre-line rounded-lg bg-ink-50 px-3 py-2 text-ink-800">{submission.mentor_feedback}</p>}
                            </div>
                        )}
                    </Panel>

                    <Panel>
                        <h2 className="text-[15px] font-semibold text-ink-950">Activity</h2>
                        <ol className="mt-3 space-y-3">
                            {[...(submission.activity || [])].reverse().map((a, i) => (
                                <li key={i} className="flex gap-2.5">
                                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${i === 0 ? 'bg-brand-700' : 'bg-ink-300'}`} />
                                    <div>
                                        <p className="text-sm font-medium text-ink-900">
                                            {ACTIVITY_LABELS[a.type] || a.type}
                                            {a.type === 'checklist_completed' && a.note ? ` (${a.note.split(':')[1]}/${a.note.split(':')[1]})` : ''}
                                        </p>
                                        <p className="text-xs text-ink-500">{formatDate(a.at, 'd MMM yyyy, h:mm a')}{a.by ? ` · ${a.by}` : ''}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Panel>
                </aside>
            </div>
        </div>
    );
}

function Glance({ challenge, submission }) {
    const score = submission.score;
    const opinionField = allFields(challenge).map(({ field }) => field).find((f) => f.glance);
    const opinion = opinionField && (opinionField.options || []).find((o) => o.value === (submission.answers[opinionField.key] || {}).value);
    if (!score && !opinion) {
        return (
            <Panel>
                <h2 className="text-[15px] font-semibold text-ink-950">At a glance</h2>
                <p className="mt-2 text-sm text-ink-600">{submission.progress.percent}% of required answers complete · {submission.files.length} file{submission.files.length === 1 ? '' : 's'}</p>
            </Panel>
        );
    }
    const naRows = score ? score.rows.filter((r) => r.na) : [];
    return (
        <Panel>
            <h2 className="text-[15px] font-semibold text-ink-950">Score at a glance</h2>
            <div className="mt-3 grid grid-cols-3 gap-2">
                {score && (
                    <>
                        <div className="rounded-xl bg-ink-50 px-3 py-2.5"><p className="text-lg font-bold text-ink-950">{score.total} / {score.max}</p><p className="text-xs text-ink-500">Total</p></div>
                        <div className="rounded-xl bg-ink-50 px-3 py-2.5"><p className="text-lg font-bold text-ink-950">{READINESS_LABELS[score.level] || '—'}</p><p className="text-xs text-ink-500">Level</p></div>
                    </>
                )}
                {opinion && (
                    <div className="rounded-xl bg-ink-50 px-3 py-2.5"><p className="text-lg font-bold text-ink-950">{opinion.value}</p><p className="text-xs text-ink-500">Opinion: {opinion.short || opinion.label}</p></div>
                )}
            </div>
            {naRows.length > 0 && (
                <p className="mt-3 text-xs text-ink-500">{naRows.length} area{naRows.length === 1 ? '' : 's'} marked N/A ({naRows.map((r) => r.short_label).join(', ')})</p>
            )}
        </Panel>
    );
}

function CommentList({ comments, userId, onRemove }) {
    return comments.map((c) => (
        <div key={c.id} className="mt-2 flex items-start justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-950">
            <p>
                <span className="font-semibold">{Number(c.author_id) === Number(userId) ? 'Your comment' : `${c.author_name}`}:</span> {c.text}
            </p>
            {Number(c.author_id) === Number(userId) && (
                <button type="button" onClick={() => onRemove(c.id)} aria-label="Delete comment" className="shrink-0 text-sky-700 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                </button>
            )}
        </div>
    ));
}

function CommentComposer({ onAdd, onCancel, placeholder }) {
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!text.trim()) return;
        setSaving(true);
        const ok = await onAdd(text.trim());
        setSaving(false);
        if (ok) { setText(''); onCancel(); }
    };
    return (
        <div className="mt-2 space-y-2">
            <textarea
                autoFocus rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
                className="w-full resize-y rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
            />
            <div className="flex gap-2">
                <button type="button" onClick={submit} disabled={saving || !text.trim()} className="btn-primary disabled:opacity-50">{saving ? 'Saving…' : 'Add comment'}</button>
                <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
            </div>
        </div>
    );
}

function ReviewField({ field, answer, files, comments, canComment, userId, onAdd, onRemove }) {
    const [composing, setComposing] = useState(false);
    return (
        <div className="group border-b border-border/70 py-3 last:border-b-0">
            <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-semibold text-ink-700">{field.label}</p>
                {canComment && !composing && (
                    <button type="button" onClick={() => setComposing(true)} className="shrink-0 text-xs font-medium text-brand-700 opacity-70 hover:underline group-hover:opacity-100">
                        Comment
                    </button>
                )}
            </div>
            <div className="mt-1"><AnswerView field={field} answer={answer} files={files} /></div>
            <CommentList comments={comments} userId={userId} onRemove={onRemove} />
            {composing && <CommentComposer onAdd={onAdd} onCancel={() => setComposing(false)} placeholder={`Comment on "${field.label}"`} />}
        </div>
    );
}

function SectionCommentBox({ comments, canComment, userId, onAdd, onRemove }) {
    const [composing, setComposing] = useState(false);
    return (
        <div className="pt-2">
            <CommentList comments={comments} userId={userId} onRemove={onRemove} />
            {canComment && (composing
                ? <CommentComposer onAdd={onAdd} onCancel={() => setComposing(false)} placeholder="Comment on this part" />
                : <button type="button" onClick={() => setComposing(true)} className="btn-secondary mt-3">+ Add comment</button>)}
        </div>
    );
}
